<template>
    <div>
        <b-table-simple small bordered>
            <thead>
                <tr>
                    <th>Status</th>
                    <th>Days</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td><strong><em>Default</em></strong></td>
                    <td>
                        <b-input type="number" :value="getDaysForDefault()" @input="daysChange($event, null)"
                            :placeholder="defaultDays?.toString()" min="1" @keydown="preventNegative" />
                    </td>
                </tr>
                <tr v-for="status in statuses" :key="status.value">
                    <td>{{ status.text }}</td>
                    <td>
                        <b-input type="number" :value="getDaysForStatus(status)"
                            @input="daysChange($event, status.value)" :placeholder="defaultDays?.toString()" min="1"
                            @keydown="preventNegative" />
                    </td>
                </tr>
            </tbody>
        </b-table-simple>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { DEFAULT_NEXT_TOUCH_DAYS, PrescriberFollowUpStatus, isOpenFollowUpStatus } from "@/models/PrescriberFollowUpCase";
import { enumToOptions } from "@/util/EnumOptions";
import TextUtils from "@/util/TextUtils";

@Component({
    name: "DoctorFollowUpNextTouchField",
})
export default class DoctorFollowUpNextTouchField extends Vue {
    @Prop() value!: any;

    /**
     * What a blank field resolves to: the configured Default row, or — with no default row either —
     * the system fallback SQL applies. The placeholder used to read "1", a value nothing in the
     * resolution chain would ever produce.
     */
    get defaultDays() {
        return this.getDaysForDefault() ?? DEFAULT_NEXT_TOUCH_DAYS;
    }

    // Derived from the enum rather than listed by hand: a status added to PrescriberFollowUpStatus
    // must get a configurable hold duration too, and a hardcoded list silently omits it. Terminal
    // statuses are then filtered back out: the view NULLs NextTouchDate outside the open pair, so a
    // row configured for Canceled or Denied could never take effect.
    get statuses() {
        return enumToOptions(PrescriberFollowUpStatus)
            .filter(o => isOpenFollowUpStatus(o.value as PrescriberFollowUpStatus))
            .map(o => ({ value: o.value, text: TextUtils.camelCaseToNormal(o.text) }));
    }

    daysChange(days: number | string, status: PrescriberFollowUpStatus | null) {
        const newValue = this.value.filter((i: any) => {
            if (status === null) return i.status !== null;
            return i.status !== status;
        });

        if (days !== "" && days !== null && days !== undefined && !isNaN(Number(days))) {
            // Floored at 1, not 0. The serializer omits default-valued properties, so a zero day count
            // is dropped from the stored JSON entirely and the row reads back as unconfigured — the
            // case would then wait the system fallback rather than the "return immediately" the entry
            // appears to ask for. Clear the field to unset a status instead.
            const numDays = Math.max(1, Math.ceil(Number(days)));
            newValue.push({ status, days: numDays });
        }
        this.$emit("input", newValue);
    }

    preventNegative(event: KeyboardEvent) {
        if (event.key === '-' || event.key === 'Minus') {
            event.preventDefault();
        }
    }

    getDaysForStatus(status: any) {
        return this.value.find((i: any) => i.status === status.value)?.days;
    }

    getDaysForDefault() {
        return this.value.find((i: any) => i.status === null)?.days;
    }
}

</script>

<style scoped lang="scss">
::v-deep(thead th),
::v-deep(td) {
    padding: 1px 7px !important;
}

::v-deep(td) {
    height: 38px !important;
}

table ::v-deep(.actions) {
    width: 50px;
}

table ::v-deep(.active) {
    width: 40px;
}
</style>
